var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, Fragment, memo, useRef, useCallback, } from 'react';
import { Box, Paper, Typography, Grid, IconButton, Avatar, Tooltip, } from '@mui/material';
import { ContentCopy, Loop, SubdirectoryArrowRight, FileDownloadOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import MarkDown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import rehypeKatex from 'rehype-katex';
import moment from 'moment';
// Internal dependencies
import { appStyles } from '../App.styles';
import { UserContext } from '../auth';
import { axiosClient } from '../axios';
import { chatStyles } from './Chat.styles';
import { CHAT_ROLES, LOG_LEVELS, SERVICES, ENDPOINTS, CHAT_CONTEXT, } from '../constants';
import { preprocessLaTeX } from './preprocessLaTeX';
import { messagesBoxStyles } from './messagesBox.styles';
/**
 * Use refs hook
 * @returns [getRef, setRef]
 */
const useRefs = () => {
    const /** List of ref */ refs = useRef([]);
    /** Get a ref from an index */
    const getRef = useCallback((idx) => (refs.current[idx]), [refs]);
    /** Set a ref with an index */
    const setRef = useCallback((idx, el) => (refs.current[idx] = el), [refs]);
    return [getRef, setRef];
};
/**
 * Messages Box component
 * @param {{messages: any, messagesLength: number}} props
 * @returns {JSX.Element}
 */
export const MessagesBox = memo(({ messages, brandVoiceContent, sendMessage }) => {
    // Get user credential
    const user = useContext(UserContext);
    // Get translatiom function
    const { t, i18n } = useTranslation();
    const [getRef, setRef] = useRefs();
    /**
     * Copy chat message
     * @param {string} text
     */
    const copyContent = (contentRef) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!contentRef) {
                return;
            }
            const contentHTMLString = contentRef.outerHTML;
            const contentPlainTextString = contentRef.innerText;
            yield navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([contentHTMLString], {
                        type: 'text/html',
                    }),
                    'text/plain': new Blob([contentPlainTextString], {
                        type: 'text/plain',
                    }),
                }),
            ]);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `MessagesBox, copyContent, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    /**
     * Regenerate content for chat
     * @returns Promise of success/fail
     */
    const regenerateContent = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            const /** Last user's message */ lastUserMessage = messages.findLast((message) => ((message === null || message === void 0 ? void 0 : message.role) === 'user'));
            // Get message text
            let contentText = '';
            const attachFiles = lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.attachFiles;
            const metadata = lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.metadata;
            if (typeof (lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content) === 'string') {
                // content is a string
                contentText = lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content;
            }
            else {
                // content is a array
                contentText = lastUserMessage.content[0].text;
            }
            // Send message
            yield sendMessage(contentText, metadata, brandVoiceContent, attachFiles, CHAT_CONTEXT.CHAT);
            // Success
            return true;
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `MessagesBox, regenerateContent, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                // If yes, notify user with the server error message
                toast.error((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
                return false;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
            return false;
        }
        ;
    }), [messages.length]);
    const downloadImage = (event, imageURL) => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e, _f;
        try {
            // Stop click propagation
            event.stopPropagation();
            // Get image
            const res = yield axiosClient.get(`${imageURL}?${moment().valueOf()}`, { responseType: 'arraybuffer' });
            // Convert image to base64 URL
            const base64URL = window.URL.createObjectURL(new Blob([res === null || res === void 0 ? void 0 : res.data]));
            // Get image extention
            const extension = `.${imageURL.split('.').pop()}`;
            // Download the image
            var link = window.document.createElement('a');
            link.download = `${moment().valueOf()}${extension}`;
            link.href = base64URL;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `MessagesBox, downloadImage, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_DESIGNER,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.status) {
                // If yes, notify user with the server error message
                toast.error((_f = (_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.message);
                return;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    return (_jsx(Fragment, { children: messages.map((message, idx) => {
            var _a, _b, _c;
            const /** string */ messageRole = message.role;
            const /** InfobaseFileType[] */ attachFiles = (message === null || message === void 0 ? void 0 : message.attachFiles) || [];
            let /** Main message content */ content = '';
            let /** Sub message content */ subContent = undefined;
            const images = (message === null || message === void 0 ? void 0 : message.images) || [];
            if (!((_a = message === null || message === void 0 ? void 0 : message.metadata) === null || _a === void 0 ? void 0 : _a.command)) {
                // If the message is from chat, not editor commands
                content = typeof (message === null || message === void 0 ? void 0 : message.content) === 'string' ? message === null || message === void 0 ? void 0 : message.content : message.content[0].text;
            }
            else {
                // If the message is from editor commands
                content = t((_b = message === null || message === void 0 ? void 0 : message.metadata) === null || _b === void 0 ? void 0 : _b.command);
                subContent = (_c = message === null || message === void 0 ? void 0 : message.metadata) === null || _c === void 0 ? void 0 : _c.targetText;
            }
            return (_jsx(Grid
            // Messages flex item
            , Object.assign({ 
                // Messages flex item
                item: true, xs: 12 }, { children: _jsxs(Paper
                // Message paper
                , Object.assign({ 
                    // Message paper
                    sx: messageRole === CHAT_ROLES.USER ? chatStyles.userMessageContainer : chatStyles.aiMessageContainer }, { children: [_jsxs(Grid, Object.assign({ container: true, spacing: 2 }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'flex-start', flexShrink: '0', } }, { children: _jsx(Avatar
                                    // Message avatar
                                    , Object.assign({ 
                                        // Message avatar
                                        sx: {
                                            width: '2rem',
                                            height: '2rem',
                                            background: messageRole === CHAT_ROLES.USER ? 'linear-gradient(0deg, rgba(0, 107, 84, 0.05), rgba(0, 107, 84, 0.05)), #F8FAF7;' : '#4B635A'
                                        } }, { children: messageRole === CHAT_ROLES.USER ? null : 'AI' })) })), _jsxs(Grid, Object.assign({ container: true, item: true, xs: true, sx: {
                                        display: 'flex',
                                        flexDirention: 'row',
                                        alignItems: 'center',
                                        flexShrink: '2',
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        overflow: 'auto',
                                    } }, { children: [_jsxs(Grid, Object.assign({ item: true, container: true, ref: (el) => { setRef(idx, el); } }, { children: [_jsxs(Grid, Object.assign({ item: true, xs: 12, sx: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' } }, { children: [_jsx(Box, Object.assign({ component: (message === null || message === void 0 ? void 0 : message.role) === 'user' ? 'div' : MarkDown, remarkPlugins: [remarkMath, remarkGfm], rehypePlugins: [rehypeKatex], 
                                                            // Message text
                                                            sx: Object.assign(Object.assign(Object.assign({}, appStyles.text16), messagesBoxStyles.message), { whiteSpace: (message === null || message === void 0 ? void 0 : message.role) === 'user' ? 'pre-wrap' : 'none' }) }, { children: preprocessLaTeX(content) })), subContent ? (_jsxs(Grid, Object.assign({ container: true }, { children: [_jsxs(Grid, Object.assign({ item: true }, { children: [" ", _jsx(SubdirectoryArrowRight, { sx: { fontSize: '16px' } }), " "] })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Box, Object.assign({ component: (message === null || message === void 0 ? void 0 : message.role) === 'user' ? 'div' : MarkDown, remarkPlugins: [remarkMath, remarkGfm], rehypePlugins: [rehypeKatex], 
                                                                        // Message text
                                                                        sx: Object.assign(Object.assign(Object.assign({}, appStyles.text12), messagesBoxStyles.message), { whiteSpace: (message === null || message === void 0 ? void 0 : message.role) === 'user' ? 'pre-wrap' : 'none' }) }, { children: preprocessLaTeX(subContent) })) }))] }))) : _jsx(_Fragment, {})] })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: (message === null || message === void 0 ? void 0 : message.role) === CHAT_ROLES.ASSISTANT &&
                                                        images.map((image) => {
                                                            const imageURL = image.url;
                                                            return (_jsx(Grid, Object.assign({ container: true, sx: {
                                                                    width: 'auto',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'stretch',
                                                                    padding: '0.375rem 0.5rem',
                                                                    margin: '1rem 0.5rem 0 0',
                                                                } }, { children: _jsxs(Grid, Object.assign({ item: true, sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        overflow: 'hidden',
                                                                        position: 'relative',
                                                                    } }, { children: [_jsx(IconButton, Object.assign({ id: "download-button", color: "primary", sx: {
                                                                                display: 'inline-flex',
                                                                                position: 'absolute',
                                                                                top: '0.5rem',
                                                                                right: '0.5rem',
                                                                                backgroundColor: '#006c49',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#006c49',
                                                                                },
                                                                            }, onClick: (event) => { downloadImage(event, imageURL); } }, { children: _jsx(FileDownloadOutlined, { sx: { color: '#ffffff' } }) })), _jsx(Box, { component: 'img', src: imageURL, sx: {
                                                                                width: '100%',
                                                                                borderRadius: '16px',
                                                                            } })] })) }), imageURL));
                                                        }) }))] })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: (message === null || message === void 0 ? void 0 : message.role) === 'user' &&
                                                attachFiles.map((attachFile) => {
                                                    const fileName = attachFile === null || attachFile === void 0 ? void 0 : attachFile.name;
                                                    const fileType = (fileName || '').split('.').pop();
                                                    return (_jsx(Grid, Object.assign({ container: true, sx: {
                                                            width: 'auto',
                                                            display: 'inline-flex',
                                                            alignItems: 'stretch',
                                                            border: '1px solid #707974',
                                                            borderRadius: '1.875rem',
                                                            padding: '0.375rem 0.5rem',
                                                            margin: '1rem 0.5rem 0 0',
                                                        } }, { children: _jsx(Grid, Object.assign({ item: true, sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                overflow: 'hidden',
                                                                maxWidth: '150px',
                                                            } }, { children: _jsx(Typography, Object.assign({ sx: {
                                                                    overflow: 'hidden',
                                                                    whiteSpace: 'nowrap',
                                                                    textOverflow: 'ellipsis',
                                                                } }, { children: fileName })) })) }), attachFile === null || attachFile === void 0 ? void 0 : attachFile._id));
                                                }) }))] }))] })), _jsx(Grid, Object.assign({ item: true }, { children: (message === null || message === void 0 ? void 0 : message.role) !== 'user' && (_jsxs(Grid, Object.assign({ container: true, columnSpacing: 1, sx: {
                                    justifyContent: 'flex-end',
                                    flexDirection: 'row',
                                    padding: '0.5rem 0 0',
                                } }, { children: [idx === messages.length - 1
                                        && (_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Tooltip, Object.assign({ title: t('Regenerate'), placement: 'top', slotProps: {
                                                    tooltip: {
                                                        sx: Object.assign({}, chatStyles.tooltip)
                                                    }
                                                } }, { children: _jsx(IconButton, Object.assign({ "aria-label": 'Regenerate button', onClick: regenerateContent }, { children: _jsx(Loop, { sx: { color: 'blackDark.main' } }) })) })) }))), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(Tooltip, Object.assign({ title: t('Copy'), placement: 'top', slotProps: {
                                                tooltip: {
                                                    sx: Object.assign({}, chatStyles.tooltip)
                                                }
                                            } }, { children: _jsx(IconButton
                                            // Copy button
                                            , Object.assign({ 
                                                // Copy button
                                                onClick: () => copyContent(getRef(idx, undefined)), "aria-label": "Copy button" }, { children: _jsx(ContentCopy, { sx: { color: '#191C1A' } }) })) })) }))] }))) }))] })) }), message._id ? message._id : `${content}-${idx}`));
        }) }));
});
